import React, { useRef, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { computeTooltipPlacement } from './calc_components/tooltipPositioning';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minMoney = 0;
const maxMoney = 1000000000;
const moneyStep = 100000;
const emiStep = 5000;
const maxEmi = 5000000;

const minPct = 0;
const maxIncomeGrowth = 20;
const maxExpenseGrowth = 15;
const pctStep = 0.5;
const incomeGrowthStep = 1;

const minInterestRate = 6;
const maxInterestRate = 50;
const interestRateStep = 0.25;

const minTenureYears = 5;
const maxTenureYears = 30;
const minLifeEventYear = 1;
const maxLifeEventYearOptions = 10;
const defaultLoanAmount = 10000000;

const POST_MOVE_CHANGE_OPTIONS = [
  { value: 'No change', label: 'No change' },
  { value: 'Increase by', label: 'Increase by' },
  { value: 'Decrease by', label: 'Decrease by' },
];

const LIFE_CHANGE_OPTIONS = POST_MOVE_CHANGE_OPTIONS;

const MOVE_IN_OPTIONS = [
  { value: '0', label: 'Immediately' },
  { value: '1', label: 'Within 1 year' },
  { value: '2', label: 'Within 2 years' },
  { value: '3', label: 'Within 3 years' },
  { value: '4', label: 'Within 4 years' },
  { value: '5', label: 'Within 5 years' },
];

function formatOrdinalYear(y) {
  const n = Number(y);
  const mod100 = n % 100;
  const mod10 = n % 10;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    if (mod10 === 1) suffix = 'st';
    else if (mod10 === 2) suffix = 'nd';
    else if (mod10 === 3) suffix = 'rd';
  }
  return `${n}${suffix} yr`;
}

const LIFE_EVENT_YEAR_OPTIONS = Array.from(
  { length: maxLifeEventYearOptions },
  (_, i) => {
    const y = i + minLifeEventYear;
    return { value: String(y), label: formatOrdinalYear(y) };
  },
);

/** Matches Excel defaults from “How much loan you can afford.xlsx”. */
const STANDALONE_DEFAULTS = {
  annualTakeHome: 3000000,
  annualExpenses: 1500000,
  incomeGrowthPct: 0,
  expenseGrowthPct: 6,
  moveInYears: 0,
  postMoveExpenseChange: 'Increase by',
  postMoveExpenseAmount: 110000,
  hasLifeEvent: false,
  lifeEventYear: 5,
  lifeEventIncomeChange: 'No change',
  lifeEventIncomeAmount: 2000000,
  lifeEventExpenseChange: 'Increase by',
  lifeEventExpenseAmount: 0,
  tenureYears: 20,
  interestRatePct: 9,
  monthlyEmi: 100000,
  loanSizingMode: 'emi',
  loanAmount: defaultLoanAmount,
};

const ARTICLE_EMBED_DEFAULTS = { ...STANDALONE_DEFAULTS };

function getDefaults(embed) {
  return embed ? ARTICLE_EMBED_DEFAULTS : STANDALONE_DEFAULTS;
}

function formatPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
}

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRatePct;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(
    maxInterestRate,
    Math.max(minInterestRate, Number(snapped.toFixed(2))),
  );
}

function snapPct(n, max, step = pctStep) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return 0;
  const s = Number(step) || pctStep;
  const snapped = Math.round(raw / s) * s;
  return Math.min(max, Math.max(minPct, Number(snapped.toFixed(2))));
}

function ModeSwitchLink({ children, onClick }) {
  return (
    <span
      className="hla-mode-switch-link"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </span>
  );
}

function MoneyField({
  id,
  label,
  labelAria,
  tooltip,
  value,
  setValue,
  wordsValue,
  setWordsValue,
  max = maxMoney,
  step = moneyStep,
  hideLabel = false,
  disabled = false,
  isDefaultValue = false,
  highlightComputed = false,
}) {
  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();
  const isComputedResult = disabled && highlightComputed;
  const words =
    wordsValue > 0
      ? inrAmountInWordsShort(
          isComputedResult
            ? resolveAmount(wordsValue)
            : Math.round(wordsValue),
        )
      : '';

  return (
    <div
      className={`savings-goal-first-year-exp-field${
        hideLabel ? ' hla-amount-inline' : ''
      }${disabled ? ' hla-money-field--disabled' : ''}${
        isComputedResult ? ' hla-money-field--computed' : ''
      }`}
    >
      <EngagerSliderRow
        id={id}
        label={hideLabel ? '' : label}
        labelAria={
          hideLabel
            ? undefined
            : labelAria || (typeof label === 'string' ? label : undefined)
        }
        tooltip={hideLabel ? undefined : tooltip}
        value={Math.min(value, max)}
        setValue={(v) => {
          if (disabled) return;
          const next = Math.min(max, Math.max(0, v));
          setValue(next);
          setWordsValue(next);
        }}
        onLiveValueChange={(n) => {
          if (disabled) return;
          if (n != null && Number.isFinite(n)) {
            setWordsValue(Math.min(max, Math.max(0, n)));
          } else {
            setWordsValue(0);
          }
        }}
        min={minMoney}
        max={max}
        step={step}
        format={(v) =>
          isComputedResult ? formatInr(v) : formatterINR.format(v)
        }
        variant="inr"
        disabled={disabled}
        isDefaultValue={isDefaultValue}
      />
      {words ? (
        <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
          {words}
        </p>
      ) : null}
    </div>
  );
}

function SelectWithInlineAmount({
  id,
  label,
  tooltip,
  value,
  onChange,
  options,
  showAmount,
  amount,
  setAmount,
  amountWords,
  setAmountWords,
  amountStep = 10000,
  isDefaultSelect = false,
  isDefaultAmount = false,
}) {
  const tooltipId = `${id}-tip`;
  const tip = tooltip != null && String(tooltip).trim() !== '';
  const tooltipWrapRef = useRef(null);
  const [tooltipPosClass, setTooltipPosClass] = useState('');
  const [tooltipInlineStyle, setTooltipInlineStyle] = useState(undefined);
  const words =
    amountWords > 0 ? inrAmountInWordsShort(Math.round(amountWords)) : '';

  const updateTooltipPosition = () => {
    const placement = computeTooltipPlacement(tooltipWrapRef.current);
    setTooltipPosClass(placement.tooltipPosClass);
    setTooltipInlineStyle(placement.tooltipInlineStyle);
  };

  return (
    <div
      className={`hla-post-move-combo${
        showAmount ? '' : ' hla-post-move-combo--solo'
      }`}
    >
      <div className="hla-post-move-combo__label nps-ups-field-label">
        <span className="nps-ups-step-label">{label}</span>
        {tip ? (
          <span
            ref={tooltipWrapRef}
            className={`nps-ups-tooltip-wrap ${tooltipPosClass}`.trim()}
          >
            <button
              type="button"
              className="btn-small-round nps-ups-q"
              aria-describedby={tooltipId}
              aria-label={`Tip for ${label}`}
              onMouseEnter={updateTooltipPosition}
              onFocus={updateTooltipPosition}
            >
              ?
            </button>
            <span
              className="nps-ups-tooltip-bubble"
              id={tooltipId}
              role="tooltip"
              style={tooltipInlineStyle}
            >
              {tooltip}
            </span>
          </span>
        ) : null}
      </div>

      <div className="hla-post-move-combo__controls">
        <div className="form-field-stack nps-ups-slider-col nps-ups-select-col">
          <select
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            className={isDefaultSelect ? 'nps-ups-select--default' : undefined}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {showAmount ? (
          <div className="hla-post-move-combo__amount hla-amount-inline">
            <EngagerSliderRow
              id={`${id}-amount`}
              label=""
              value={Math.min(amount, maxMoney)}
              setValue={(v) => {
                const next = Math.min(maxMoney, Math.max(0, v));
                setAmount(next);
                setAmountWords(next);
              }}
              onLiveValueChange={(n) => {
                if (n != null && Number.isFinite(n)) {
                  setAmountWords(Math.min(maxMoney, Math.max(0, n)));
                } else {
                  setAmountWords(0);
                }
              }}
              min={minMoney}
              max={maxMoney}
              step={amountStep}
              isDefaultValue={isDefaultAmount}
              format={(v) => formatterINR.format(v)}
              variant="inr"
            />
            {words ? (
              <p className="hla-post-move-combo__words calc-output-amount-in-words savings-goal-first-year-exp-words">
                {words}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SectionHeading({ children, tooltip, tipId = 'hla-section-tip' }) {
  const tip = tooltip != null && String(tooltip).trim() !== '';
  const tooltipWrapRef = useRef(null);
  const [tooltipPosClass, setTooltipPosClass] = useState('');
  const [tooltipInlineStyle, setTooltipInlineStyle] = useState(undefined);

  const updateTooltipPosition = () => {
    const placement = computeTooltipPlacement(tooltipWrapRef.current);
    setTooltipPosClass(placement.tooltipPosClass);
    setTooltipInlineStyle(placement.tooltipInlineStyle);
  };

  return (
    <h3 className="hla-section-heading">
      <span className="hla-section-heading__text">{children}</span>
      {tip ? (
        <span
          ref={tooltipWrapRef}
          className={`nps-ups-tooltip-wrap hla-section-heading__tip ${tooltipPosClass}`.trim()}
        >
          <button
            type="button"
            className="btn-small-round nps-ups-q"
            aria-describedby={tipId}
            aria-label="More information"
            onMouseEnter={updateTooltipPosition}
            onFocus={updateTooltipPosition}
          >
            ?
          </button>
          <span
            className="nps-ups-tooltip-bubble"
            id={tipId}
            role="tooltip"
            style={tooltipInlineStyle}
          >
            {tooltip}
          </span>
        </span>
      ) : null}
    </h3>
  );
}


export {
  formatterINR,
  minMoney,
  maxMoney,
  moneyStep,
  emiStep,
  maxEmi,
  minPct,
  maxIncomeGrowth,
  maxExpenseGrowth,
  pctStep,
  incomeGrowthStep,
  minInterestRate,
  maxInterestRate,
  interestRateStep,
  minTenureYears,
  maxTenureYears,
  minLifeEventYear,
  maxLifeEventYearOptions,
  defaultLoanAmount,
  POST_MOVE_CHANGE_OPTIONS,
  LIFE_CHANGE_OPTIONS,
  MOVE_IN_OPTIONS,
  LIFE_EVENT_YEAR_OPTIONS,
  STANDALONE_DEFAULTS,
  ARTICLE_EMBED_DEFAULTS,
  getDefaults,
  formatPct,
  formatInterestRatePct,
  snapInterestRate,
  snapPct,
  ModeSwitchLink,
  MoneyField,
  SelectWithInlineAmount,
  SectionHeading,
};
